import { configureStore, combineReducers } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { registerCommonSagas } from './registerSagas';
import rootReducer from './rootReducer';
import { RESET_STORE } from './actions/actionTypes';

/**
 * Configures and returns the Redux store for the application.
 *
 * - Integrates Redux Toolkit's configureStore for simplified store setup.
 * - Adds Redux Saga middleware for handling side effects.
 * - Disables thunk, immutableCheck, and serializableCheck for performance and compatibility.
 * - Enables Redux DevTools in non-production environments.
 * - Registers all root sagas using the provided registerCommonSagas function.
 *
 * @returns {import('@reduxjs/toolkit').EnhancedStore} The configured Redux store
 *
 * Usage:
 *   import store from './_-_-_-_-__-_-_-_-__configureStore';
 *   store.dispatch(action);
 * 
 *  Author: fabrice.miras@vitecsoftware.com
 *  Date: 2026-01-07
 * 
 */
function createStore() {
    // Create saga middleware for handling side effects
    const sagaMiddleware = createSagaMiddleware();
    // Combine middlewares
    const middlewares = [/*...getDefaultMiddleware(),*/ sagaMiddleware];

    // Combine all reducers
    const combinedReducer = combineReducers(rootReducer);

    // Root reducer with reset functionality
    const rootReducerWithReset = (state, action) => {
        if (action.type === RESET_STORE) {
            state = undefined;
        }
        return combinedReducer(state, action);
    };

    const store = configureStore({
        reducer: rootReducerWithReset,
        middleware: getDefaultMiddleware =>
            getDefaultMiddleware({
                thunk: false,
                immutableCheck: false,
                serializableCheck: false
            }).concat(...middlewares),
        devTools: process.env.NODE_ENV !== 'production'
    });

    // Register all root sagas
    try {
        registerCommonSagas(sagaMiddleware);
    } catch (e) {
        // eslint-disable-next-line no-console, no-undef
        console.error('Saga registration failed:', e);
    }
    return store;
}

// Create singleton store instance
const storeInstance = createStore();

export default storeInstance;

export function getStore() {
    return storeInstance;
}